/**
 * SearchTipsService
 * 
 * Provides dynamic, contextual search tips to help users improve their search queries.
 * This service is designed to be extensible and does not rely on hardcoded entity data.
 * 
 * @module services/searchTipsService
 */

/**
 * Analyzes a search query and generates contextual tips
 * @param {string} query - The search query to analyze
 * @returns {Array<string>} Array of helpful search tips
 */
export const getSearchTips = (query) => {
    if (!query || typeof query !== 'string') {
        return getDefaultTips();
    }

    const normalizedQuery = query.trim().toLowerCase();
    const tips = [];

    // Analyze query characteristics
    const isShort = normalizedQuery.length < 3;
    const isVeryLong = normalizedQuery.length > 50;
    const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(normalizedQuery);
    const wordCount = normalizedQuery.split(/\s+/).filter(w => w.length > 0).length;
    const isSingleWord = wordCount === 1;
    const hasNumbers = /\d/.test(normalizedQuery);

    // Generate contextual tips based on query analysis
    if (isShort) {
        tips.push("Try using more specific keywords or the full name");
    }

    if (isVeryLong) {
        tips.push("Consider using shorter, more focused search terms");
    }

    if (hasSpecialChars) {
        tips.push("Remove special characters for better results");
    }

    if (isSingleWord && !hasNumbers) {
        tips.push("Try adding more context, like a category or additional keywords");
        tips.push("For sports teams, include the full name (e.g., 'FC Porto' instead of 'FCP')");
    }

    // General tips that apply to most searches
    const generalTips = [
        "Check your spelling and try alternative spellings",
        "Try using different keywords or synonyms",
        "Be more specific or more general depending on results",
        "Search for the category or type of content you're looking for"
    ];

    // Add general tips if we don't have enough contextual tips
    if (tips.length < 3) {
        tips.push(...generalTips.slice(0, 3 - tips.length));
    }

    // Ensure we return 3-5 tips
    return tips.slice(0, 5);
};

/**
 * Returns default search tips when query analysis isn't possible
 * @returns {Array<string>} Default search tips
 */
export const getDefaultTips = () => {
    return [
        "Try using different keywords or check your spelling",
        "Be more specific or more general depending on what you're looking for",
        "Search for the category or type of content (e.g., 'soccer', 'music', 'news')",
        "Try the full name instead of abbreviations",
        "Use multiple related keywords to improve results"
    ];
};

/**
 * Gets tips specific to error scenarios
 * @param {string} errorType - Type of error encountered
 * @param {string} query - The search query that failed
 * @returns {Array<string>} Error-specific tips
 */
export const getErrorSpecificTips = (errorType, query) => {
    const baseTips = getSearchTips(query);
    
    switch (errorType) {
        case 'NETWORK_ERROR':
            return [
                "Check your internet connection",
                "Try again in a few moments",
                ...baseTips.slice(0, 2)
            ];
        case 'TIMEOUT':
            return [
                "The search is taking longer than expected",
                "Try a simpler or more specific query",
                ...baseTips.slice(0, 2)
            ];
        case 'SEARCH_TEMPORARILY_UNAVAILABLE':
            return [
                "Search is briefly unavailable",
                "Try again in a few moments",
                ...baseTips.slice(0, 2)
            ];
        case 'NO_RESULTS':
        default:
            return baseTips;
    }
};













